import l10n from "../lang/locallang";

/**
 * Calculate height of textarea.
 * @param {HTMLElement} textareaLeft - left textarea element in current row.
 * @param {HTMLElement} textareaRight - right textarea element in current row.
 */
const calculateHeightTextarea = (textareaLeft, textareaRight) => {
    if (textareaRight) {
        const maxHeight = Math.max(textareaLeft.scrollHeight, textareaRight.scrollHeight);

        textareaLeft.style.height = `${maxHeight}px`;
        textareaRight.style.height = `${maxHeight}px`;
    } else {
        textareaLeft.style.height = `${textareaLeft.scrollHeight}px`;
    }
}

/**
 * Set auto height textarea on popup open and input event
 * @param {HTMLElement} popup - current popup.
 */
export const autoHeightTextarea = (popup) => {
    if (!popup) return;

    const rows = popup.querySelectorAll('.assessment-item-row');

    if (rows) {
        rows.forEach((row, i) => {
            const [textareaLeft, textareaRight] = row.querySelectorAll('.ah-textarea');
            calculateHeightTextarea(textareaLeft, textareaRight);

            const textareaElements = [textareaLeft, textareaRight].filter(Boolean);

            textareaElements.forEach(area => {
                area.addEventListener('input', () => {
                    calculateHeightTextarea(textareaLeft, textareaRight);
                })
            });
        });
    }
}

/**
 * Remove error message from .popup-body
 * @param {HTMLElement} popup - current popup.
 */
export const deleteErrorMessage = (popup) => {
    const  errorMessage = popup.querySelector('.cg-popup__error-message');

    if (errorMessage) {
        errorMessage.remove();
    }
}

export const deleteErrorMessageMatching = (popup) => {
    const errorMessage = popup.querySelector('.assessment-result');
    if (errorMessage) {
        errorMessage.innerHTML = '';
        let resultItems = popup.querySelectorAll('.matching-result_item');
        resultItems.forEach((item) => {
            item.classList.remove('incorrect');
            item.classList.remove('correct');
        });
    }
}

/**
 * Add error message to .popup-body
 * @param {HTMLElement} popup - current popup.
 * @param {String} message - error message text.
 */
export const showErrorMessage = (popup, message) => {
    const  errorMessage = document.createElement('p');
    const popupBody = popup.querySelector('.cg-popup__body') || popup.querySelector('.popup-body');

    errorMessage.classList.add('cg-popup__error-message');
    errorMessage.innerHTML = `${message}.`;

    if (popupBody) {
        popupBody.insertBefore(errorMessage, popupBody.firstChild);
    }
}

/**
 * Check if some of radio/checkbox checked
 * @param {HTMLElement} popup - current popup.
 */
export const isUserAnswerInputChecked = (popup) => {
    const userAnswerInput = [...popup.querySelectorAll('.choice__input')];

    return userAnswerInput.some(el => el.checked);
}

/**
 * Handle drag and drop behavior
 * @param {HTMLElement} popup - current popup.
 */
export const handleDragAndDrop = (popup) => {
    const list = popup.querySelector('.quiz-matching');
    const matchingItems = list.querySelectorAll('.matching-item');
    let draggedItem = null;
    let oldItem = null;

    // Add new drag and drop event listeners
    if(matchingItems.length === 0) return;
    matchingItems.forEach(addDragAndDropEventListeners);

    function addDragAndDropEventListeners(item) {
        item.addEventListener('dragstart', dragStart);
        item.addEventListener('dragover', dragOver);
        item.addEventListener('dragleave', dragLeave);
        item.addEventListener('drop', drop);
        item.addEventListener('dragend', dragEnd);
    }

    function dragStart(e) {
        e.dataTransfer.setData('text/plain', e.target.dataset.matchingId);
        e.dataTransfer.dropEffect = 'move';

        draggedItem = this;
        this.classList.add('dragging');
    }

    function dragOver(e) {
        e.preventDefault();
        this.classList.add('dragging-over');
    }

    function dragLeave() {
        this.classList.remove('dragging-over');
    }

    function drop(e) {
        e.preventDefault();
        oldItem = this;
        oldItem.classList.remove('dragging-over');

        const questionsList = list.querySelector('.matching-questions ul');
        const answersList = list.querySelector('.matching-answers ul');
        const draggedItemID = e.dataTransfer.getData('text/plain');
        let oldElement = null;
        let newElement = null;
        draggedItem = questionsList.querySelector(`[data-matching-id="${draggedItemID}"]`);

        if (this.classList.contains('matching-questions_item')) {
            // if draggedItem and oldItem are not equal then replace
            replaceItems(oldItem, draggedItem, oldElement, newElement, questionsList);

            // if moved to left side (-10px) and exist class dragged-right remove it
            if (event.clientX < list.getBoundingClientRect().left + list.offsetWidth / 0.9) {
                if (newElement) {
                    newElement.classList.remove('dragged-right');
                } else if (draggedItem) {
                    draggedItem.classList.remove('dragged-right');
                }
            }
        } else {
            // which number of answer in answersList
            const answerNumber = Array.prototype.indexOf.call(answersList.children, this);
            // get element in questionsList array by answerNumber and replace it in array
            const questionElement = questionsList.children[answerNumber];
            // if answer number and question number are equal then not replace
            if (questionElement === draggedItem) {
                draggedItem.classList.add('dragged-right');
            } else {
                oldItem = questionsList.children[answerNumber];
                replaceItems(oldItem, draggedItem, oldElement, newElement, questionsList);

                // add class dragged-right if moved to right side (+10px)
                if (newElement) {
                    newElement.classList.add('dragged-right');
                } else if (draggedItem) {
                    draggedItem.classList.add('dragged-right');
                }
            }
        }
    }

    function replaceItems(oldItem, draggedItem, oldElement, newElement, questionsList) {
        if (oldItem && draggedItem && oldItem !== draggedItem) {
            oldElement = oldItem.cloneNode(true);
            newElement = draggedItem.cloneNode(true);

            // if sorting in the same list
            if (questionsList.contains(draggedItem) && questionsList.contains(oldItem)) {
                questionsList.replaceChild(newElement, oldItem);
                questionsList.replaceChild(oldElement, draggedItem);
                addDragAndDropEventListeners(newElement);
                addDragAndDropEventListeners(oldElement);
            }
        }
    }

    function dragEnd() {
        let questionsList = list.querySelector('.matching-questions ul');
        if (questionsList.querySelectorAll('.dragging').length > 0) {
            questionsList.querySelectorAll('.dragging').forEach(function (item) {
                item.classList.remove('dragging');
            });
        }
    }
}

export const handleDragAndDropForNewAssessments = (popup) => {
    const list = popup.querySelector('.matching');
    const matchingItems = list.querySelectorAll('.matching__questions-item:not(.undragable), .matching__answers-item:not(.undragable)');
    let draggedItem = null;
    let oldItem = null;

    // Add new drag and drop event listeners
    if(matchingItems.length === 0) return;
    matchingItems.forEach(addDragAndDropEventListeners);

    function addDragAndDropEventListeners(item) {
        item.addEventListener('dragstart', dragStart);
        item.addEventListener('dragover', dragOver);
        item.addEventListener('dragleave', dragLeave);
        item.addEventListener('drop', drop);
        item.addEventListener('dragend', dragEnd);
    }

    function dragStart(e) {
        e.dataTransfer.setData('text/plain', e.target.dataset.matchingId);
        e.dataTransfer.dropEffect = 'move';

        draggedItem = this;
        this.classList.add('is-dragging');
    }

    function dragOver(e) {
        e.preventDefault();
        this.classList.add('is-dragging-over');
    }

    function dragLeave() {
        this.classList.remove('is-dragging-over');
    }

    function drop(e) {
        e.preventDefault();
        oldItem = this;
        oldItem.classList.remove('is-dragging-over');

        const questionsList = list.querySelector('.matching__questions');
        const answersList = list.querySelector('.matching__answers');
        const draggedItemID = e.dataTransfer.getData('text/plain');
        let oldElement = null;
        let newElement = null;
        draggedItem = questionsList.querySelector(`[data-matching-id="${draggedItemID}"]`);

        if (this.classList.contains('matching__questions-item')) {
            // if draggedItem and oldItem are not equal then replace
            replaceItems(oldItem, draggedItem, oldElement, newElement, questionsList);

            // if moved to left side (-10px) and exist class dragged-right remove it
            if (event.clientX < list.getBoundingClientRect().left + list.offsetWidth / 0.9) {
                if (newElement) {
                    newElement.classList.remove('is-dragged-right');
                } else if (draggedItem) {
                    draggedItem.classList.remove('is-dragged-right');
                }
            }
        } else {
            // which number of answer in answersList
            const answerNumber = Array.prototype.indexOf.call(answersList.children, this);
            // get element in questionsList array by answerNumber and replace it in array
            const questionElement = questionsList.children[answerNumber];
            // if answer number and question number are equal then not replace
            if (questionElement === draggedItem) {
                draggedItem.classList.add('is-dragged-right');
            } else {
                oldItem = questionsList.children[answerNumber];
                replaceItems(oldItem, draggedItem, oldElement, newElement, questionsList);

                // add class dragged-right if moved to right side (+10px)
                if (newElement) {
                    newElement.classList.add('is-dragged-right');
                } else if (draggedItem) {
                    draggedItem.classList.add('is-dragged-right');
                }
            }
        }
    }

    function replaceItems(oldItem, draggedItem, oldElement, newElement, questionsList) {
        if (oldItem && draggedItem && oldItem !== draggedItem) {
            oldElement = oldItem.cloneNode(true);
            newElement = draggedItem.cloneNode(true);

            // if sorting in the same list
            if (questionsList.contains(draggedItem) && questionsList.contains(oldItem)) {
                questionsList.replaceChild(newElement, oldItem);
                questionsList.replaceChild(oldElement, draggedItem);
                addDragAndDropEventListeners(newElement);
                addDragAndDropEventListeners(oldElement);
            }
        }
    }

    function dragEnd() {
        let questionsList = list.querySelector('.matching__questions');
        if (questionsList.querySelectorAll('.is-dragging').length > 0) {
            questionsList.querySelectorAll('.is-dragging').forEach(function (item) {
                item.classList.remove('is-dragging');
            });
        }
    }
}


/**
 * Set inline height to items
 * @param {HTMLElement} popup - current popup.
 */
export const setItemsHeight = (popup) => {
    const questionsItems = [...popup.querySelectorAll('.matching-questions .matching-item')];
    const answersItems = [...popup.querySelectorAll('.matching-answers .matching-item')];

    if (questionsItems && answersItems) {
        const questionsItemsMaxHeight = Math.max(...questionsItems.map(el => el.querySelector('span').clientHeight));
        const answersItemsMaxHeight = Math.max(...answersItems.map(el => el.querySelector('span').clientHeight));
        const itemMaxHeight = Math.max(questionsItemsMaxHeight, answersItemsMaxHeight);

        questionsItems.forEach((questionItem, i) => {
            const answerItem = answersItems[i];

            questionItem.style.height = `${itemMaxHeight + 24}px`; // 24 - it's top padding + top border + bottom padding + bottom border
            answerItem.style.height = `${itemMaxHeight + 24}px`; // 24 - it's top padding + top border + bottom padding + bottom border
        });
    }
}

export const setItemsHeightForNewAssessment = (popup) => {
    const questionsItems = [...popup.querySelectorAll('.matching__questions-item')];
    const answersItems = [...popup.querySelectorAll('.matching__answers-item')];

    if (questionsItems && answersItems) {
        const questionsItemsMaxHeight = Math.max(...questionsItems.map(el => el.querySelector('.matching__questions-item-text').clientHeight));
        const answersItemsMaxHeight = Math.max(...answersItems.map(el => el.querySelector('.matching__answers-item-text').clientHeight));
        const itemMaxHeight = Math.max(questionsItemsMaxHeight, answersItemsMaxHeight);

        questionsItems.forEach((questionItem, i) => {
            const answerItem = answersItems[i];

            questionItem.style.height = `${itemMaxHeight + 24}px`; // 24 - it's top padding + top border + bottom padding + bottom border
            answerItem.style.height = `${itemMaxHeight + 24}px`; // 24 - it's top padding + top border + bottom padding + bottom border
        });
    }
}

export const setMaxLength = (popup) => {
    const maxLengthInputs = popup.querySelectorAll('[data-maxlength]');

    if (maxLengthInputs) {
        maxLengthInputs.forEach(input => {
            const { maxlength } = input.dataset;
            const counter = input.parentElement.querySelector('.js-maxlength-counter');

            if (counter && maxlength) {
                counter.innerHTML = `${input.value.length}`;

                input.addEventListener('input', () => {
                    if (input.value.length > maxlength) {
                        input.value = input.value.substring(0, maxlength);
                    }
                    counter.innerHTML = `${input.value.length}`;
                });
            }
        });
    }
}

export const validateUniqueOption = (input) => {
    const { errorMessageForNotUniqueOption } = l10n.assessmentsCommon;
    const error = checkUniqueOption(input);

    if (error) {
        showError(input, errorMessageForNotUniqueOption);
    } else {
        hideError(input);
    }

    return error;
}

const checkUniqueOption = (input) => {
    const popup = document.getElementById('create-assessment-popup');
    const options = popup.querySelectorAll('.ah-textarea');
    let error = false;

    // Check if input not empty and compare all options with current option
    for (let option of options) {
        if (option.value.trim() === input.value.trim() && option !== input || input.value.trim() === '') {
            error = true;
        }
    }

    return error;
}

export const showError = (input, errorText) => {
    const errorMessage = document.createElement('div');

    errorMessage.innerHTML = errorText;
    errorMessage.classList.add('error-message-text');
    input.parentNode.classList.add('has-error');
    input.parentNode.appendChild(errorMessage);
}

export const hideError = (input) => {
    const errorMessage = input.parentNode.querySelector('.error-message-text');

    input.parentNode.classList.remove('has-error');

    if (errorMessage) {
        input.parentNode.removeChild(errorMessage);
    }
}

export const removeEmptyRow = (input) => {
    const inputRow = input.closest('.assessment-item-row');
    const rowParent = inputRow.parentNode;
    const allRows = rowParent.querySelectorAll('.assessment-item-row');

    if (allRows.length > 1) {
        rowParent.removeChild(inputRow);
    }
}
